/** Loading, refreshing and failing, the same way on every screen. */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { GatewayError } from '@p1plug/platform';
import { ErrorState, type ErrorStateProps } from './ds';
import { Skeleton } from './layout';

export interface Resource<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  /** True when a refresh failed and `data` is the last good answer. */
  stale: boolean;
  updatedAt: Date | null;
  reload: () => void;
}

/**
 * Loads a value and optionally refreshes it. A failed refresh keeps the last
 * good value and marks it stale: live screens show last-known data with its
 * age rather than going blank.
 */
export function useResource<T>(
  load: () => Promise<T>,
  deps: unknown[],
  options: { refreshMs?: number } = {},
): Resource<T> {
  const [state, setState] = useState<Omit<Resource<T>, 'reload'>>({
    data: undefined,
    error: null,
    loading: true,
    stale: false,
    updatedAt: null,
  });
  const loader = useRef(load);
  loader.current = load;
  const run = useCallback((reset: boolean) => {
    let cancelled = false;
    if (reset) setState({ data: undefined, error: null, loading: true, stale: false, updatedAt: null });
    loader.current().then(
      (data) => !cancelled && setState({ data, error: null, loading: false, stale: false, updatedAt: new Date() }),
      (error: unknown) => !cancelled && setState((s) => ({ ...s, error, loading: false, stale: s.data !== undefined })),
    );
    return () => {
      cancelled = true;
    };
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass the values their loader depends on
  useEffect(() => run(true), deps);
  useEffect(() => {
    if (!options.refreshMs) return;
    const timer = setInterval(() => document.visibilityState === 'visible' && run(false), options.refreshMs);
    return () => clearInterval(timer);
  }, [options.refreshMs, run]);
  return { ...state, reload: () => run(false) };
}

/** The current time, re-rendering on an interval, for "… ago" texts. */
export function useNow(intervalMs = 1000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** What failed, what it affects, what to do, and the reference: for any platform error. */
export function describeError(error: unknown, what: string, retry?: () => void): ErrorStateProps {
  const e = error instanceof GatewayError ? error : null;
  const technical: [string, string][] = e
    ? [
        ['Request', e.request],
        ['Status', e.status === 0 ? 'no response' : String(e.status)],
        ['Error code', e.code],
        ['Message', e.message],
      ]
    : [['Message', error instanceof Error ? error.message : String(error)]];
  const tryAgain = retry ? [{ label: 'Try again', onClick: retry }] : [];
  if (e && (e.status === 401 || e.status === 403)) {
    return {
      tone: 'warning',
      headline: `This application is not authorised to read ${what}`,
      impact: 'The platform refused the request. Nothing is wrong with the data itself.',
      actionsLabel: 'What to check',
      steps: [
        'The gateway in front of this application adds the credential; check that it is configured (P1PLUG_ADMIN_TOKEN, or the tenant API key for customer surfaces).',
        'A credential without the required scope answers 403: compare its scopes with the operation.',
      ],
      actions: tryAgain,
      technical,
    };
  }
  if (e && e.status === 0) {
    return {
      headline: 'The platform cannot be reached',
      impact: `${what[0]!.toUpperCase()}${what.slice(1)} cannot be shown right now. Devices keep measuring and buffering; nothing is lost.`,
      actionsLabel: 'What to check',
      steps: ['Your network connection.', 'Whether the platform is running (status.p1plug.nl).'],
      actions: tryAgain,
      technical,
    };
  }
  if (e && e.status === 404) {
    return {
      tone: 'unknown',
      headline: `We could not find ${what}`,
      impact: 'It may have been removed, or the link is from another environment.',
      technical,
    };
  }
  return {
    headline: `We could not load ${what}`,
    impact: e?.retryable
      ? 'The platform answered with an error. This is usually temporary.'
      : 'The platform rejected the request.',
    actions: tryAgain,
    escalation: 'If this continues, pass the technical details to P1Plug engineering.',
    technical,
  };
}

/** Renders a resource: a skeleton first, an explained error on failure, the content otherwise. */
export function ResourceView<T>({
  resource,
  what,
  children,
  skeleton,
}: {
  resource: Resource<T>;
  what: string;
  children: (data: T) => ReactNode;
  skeleton?: ReactNode;
}) {
  if (resource.data !== undefined) return <>{children(resource.data)}</>;
  if (resource.loading) return <>{skeleton ?? <Skeleton lines={4} />}</>;
  return <ErrorState {...describeError(resource.error, what, resource.reload)} />;
}
